import { apiFetch } from '@/lib/api-client/client';

/**
 * Client for GET/PATCH /api/guilds/:guildId/config/:module and
 * POST /api/guilds/:guildId/config/:module/reset (routes/guild-config.ts
 * → bridge/config.py → the bot's existing cogs/helpers → dem.db).
 *
 * Two response shapes, matching bridge/config_registry.py's two module
 * kinds:
 *   - `states`: the 11 modules whose entire config is an on/off switch
 *     (or a small set of named switches) via the existing DashboardConfig
 *     contract - reaction-roles, giveaways, leveling, backup, translation,
 *     automod, suggestions, starboard, antinuke, security, reminders.
 *   - `data`: the 6 "custom" modules with real field-level config -
 *     ai, logging, analytics, prefix, tickets, welcome.
 * A page only ever reads the one it expects for its module; both are
 * typed as loosely-shaped records here (not per-field interfaces) so
 * this file doesn't have to be updated every time a module's field list
 * changes - callers narrow to what they need.
 */

export type GuildConfigModule =
  | 'reaction-roles' | 'giveaways' | 'leveling' | 'backup' | 'translation'
  | 'automod' | 'suggestions' | 'starboard' | 'antinuke' | 'security' | 'reminders'
  | 'ai' | 'logging' | 'analytics' | 'prefix' | 'tickets' | 'welcome';

export interface GuildConfigResponse<TData = Record<string, unknown>> {
  guildId: string;
  module: GuildConfigModule;
  states?: Record<string, boolean>;
  data?: TData;
}

export function getGuildConfig<TData = Record<string, unknown>>(
  guildId: string,
  module: GuildConfigModule,
): Promise<GuildConfigResponse<TData>> {
  return apiFetch<GuildConfigResponse<TData>>(`/api/guilds/${guildId}/config/${module}`);
}

export function patchGuildConfig<TData = Record<string, unknown>>(
  guildId: string,
  module: GuildConfigModule,
  changes: Record<string, unknown>,
): Promise<GuildConfigResponse<TData>> {
  return apiFetch<GuildConfigResponse<TData>>(`/api/guilds/${guildId}/config/${module}`, {
    method: 'PATCH',
    body: JSON.stringify({ changes }),
  });
}

export function resetGuildConfig<TData = Record<string, unknown>>(
  guildId: string,
  module: GuildConfigModule,
): Promise<GuildConfigResponse<TData>> {
  return apiFetch<GuildConfigResponse<TData>>(`/api/guilds/${guildId}/config/${module}/reset`, {
    method: 'POST',
  });
}

/** Convenience for the 11 toggle-only modules - GET returns `{ states }`. */
export function getGuildToggleStates(guildId: string, module: GuildConfigModule): Promise<Record<string, boolean>> {
  return getGuildConfig(guildId, module).then((res) => res.states ?? {});
}

/** Sets the single master on/off switch for a toggle-only module. */
export function setGuildToggleAll(guildId: string, module: GuildConfigModule, value: boolean) {
  return patchGuildConfig(guildId, module, { all: value }).then((res) => res.states ?? {});
}

/** Sets one named switch within a toggle module that supports per-feature
 * state (Security's 4 protections, AutoMod's 12 protections, Logging's
 * per-event types via the "logging" custom module instead). */
export function setGuildToggleFeature(guildId: string, module: GuildConfigModule, key: string, value: boolean) {
  return patchGuildConfig(guildId, module, { states: { [key]: value } }).then((res) => res.states ?? {});
}
